import React from "react";

// ── PDF Crop Modal ────────────────────────────────────────────────────────────
function PdfCropModal({ fileUrl, language, onCapture, onClose }) {
    const canvasRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    const [pdfDoc, setPdfDoc] = React.useState(null);
    const [pageNum, setPageNum] = React.useState(1);
    const [numPages, setNumPages] = React.useState(1);
    const [rendering, setRendering] = React.useState(false);
    const containerRef = React.useRef(null);
    // Crop state
    const [cropStart, setCropStart] = React.useState(null);
    const [cropRect, setCropRect] = React.useState(null);
    const [loadError, setLoadError] = React.useState(null);
    const isDragging = React.useRef(false);

    // Load PDF
    React.useEffect(() => {
        if (!fileUrl) return;
        let cancelled = false;
        setLoadError(null);
        (async () => {
            try {
                const pdfjsLib = await import("pdfjs-dist");
                pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
                const doc = await pdfjsLib.getDocument(fileUrl).promise;
                if (!cancelled) {
                    setPdfDoc(doc);
                    setNumPages(doc.numPages);
                }
            } catch (err) {
                // A locally uploaded PDF is a blob URL and always loads; a
                // stored one is fetched from GCS, which needs CORS to allow
                // this origin. Say so instead of showing a blank canvas.
                console.warn("PdfCropModal: failed to load PDF", err);
                if (!cancelled) setLoadError(err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fileUrl]);

    // Render current page — fit-to-width
    React.useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let cancelled = false;
        setRendering(true);
        setCropRect(null);
        (async () => {
            const page = await pdfDoc.getPage(pageNum);
            if (cancelled) return;
            // Compute fit-to-width scale based on container width
            const containerWidth = containerRef.current
                ? containerRef.current.clientWidth
                : 800;
            const baseViewport = page.getViewport({ scale: 1 });
            const fitScale = containerWidth / baseViewport.width;
            const viewport = page.getViewport({
                scale: 1 * fitScale,
            });
            const canvas = canvasRef.current;
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");
            await page.render({ canvasContext: ctx, viewport }).promise;
            if (!cancelled) setRendering(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [pdfDoc, pageNum]);

    const getPos = (e, el) => {
        const rect = el.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: clientX - rect.left,
            y: clientY - rect.top,
        };
    };

    const onMouseDown = (e) => {
        e.preventDefault();
        const pos = getPos(e, overlayRef.current);
        isDragging.current = true;
        setCropStart(pos);
        setCropRect(null);
    };

    const onMouseMove = (e) => {
        if (!isDragging.current || !cropStart) return;
        const pos = getPos(e, overlayRef.current);
        setCropRect({
            x: Math.min(cropStart.x, pos.x),
            y: Math.min(cropStart.y, pos.y),
            w: Math.abs(pos.x - cropStart.x),
            h: Math.abs(pos.y - cropStart.y),
        });
    };

    const onMouseUp = () => {
        isDragging.current = false;
    };

    const handleCapture = () => {
        if (!cropRect || cropRect.w < 5 || cropRect.h < 5) return;
        const canvas = canvasRef.current;
        const overlay = overlayRef.current;
        // Scale from display pixels to canvas pixels
        const scaleX = canvas.width / overlay.clientWidth;
        const scaleY = canvas.height / overlay.clientHeight;
        const cropCanvas = document.createElement("canvas");
        cropCanvas.width = cropRect.w * scaleX;
        cropCanvas.height = cropRect.h * scaleY;
        const ctx = cropCanvas.getContext("2d");
        ctx.drawImage(
            canvas,
            cropRect.x * scaleX,
            cropRect.y * scaleY,
            cropRect.w * scaleX,
            cropRect.h * scaleY,
            0,
            0,
            cropCanvas.width,
            cropCanvas.height,
        );
        onCapture(cropCanvas.toDataURL("image/png"));
        setCropRect(null);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4">
            {/* Header */}
            <div className="w-full max-w-4xl flex items-center justify-between mb-3">
                <p className="text-white font-semibold text-sm">
                    {language === "zh"
                        ? "在 PDF 上拖曳以選取區域，然後按「截取」"
                        : "Drag on the PDF to select a region, then click Capture"}
                </p>
                <button
                    onClick={onClose}
                    className="text-white/70 hover:text-white transition-colors text-xs bg-white/10 px-3 py-1.5 rounded-lg"
                >
                    {language === "zh" ? "關閉" : "Close"}
                </button>
            </div>

            {/* Page controls */}
            <div className="flex items-center gap-3 mb-3">
                <button
                    disabled={pageNum <= 1}
                    onClick={() => setPageNum((n) => n - 1)}
                    className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 text-white rounded-lg disabled:opacity-30 transition-colors"
                >
                    ‹ {language === "zh" ? "上一頁" : "Prev"}
                </button>
                <span className="text-white text-xs">
                    {language === "zh"
                        ? `第 ${pageNum} / ${numPages} 頁`
                        : `Page ${pageNum} / ${numPages}`}
                </span>
                <button
                    disabled={pageNum >= numPages}
                    onClick={() => setPageNum((n) => n + 1)}
                    className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 text-white rounded-lg disabled:opacity-30 transition-colors"
                >
                    {language === "zh" ? "下一頁" : "Next"} ›
                </button>
            </div>

            {/* Canvas + overlay */}
            <div
                ref={containerRef}
                className="relative max-w-4xl w-full overflow-auto rounded-xl border border-white/20 bg-gray-900 max-h-[65vh]"
            >
                {rendering && !loadError && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
                {loadError && (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center">
                        <p className="text-white font-medium">
                            {language === "zh"
                                ? "無法載入此 PDF"
                                : "Could not load this PDF"}
                        </p>
                        <p className="text-white/60 text-sm max-w-md">
                            {language === "zh"
                                ? "已儲存的規格書需要允許此網站讀取才能截圖，請改用「新增圖片」上傳。"
                                : "A stored datasheet can only be captured when the bucket allows this site to read it. Use “Add Image” to upload instead."}
                        </p>
                    </div>
                )}
                <div
                    ref={overlayRef}
                    className="relative cursor-crosshair select-none"
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onTouchStart={onMouseDown}
                    onTouchMove={onMouseMove}
                    onTouchEnd={onMouseUp}
                >
                    <canvas ref={canvasRef} className="block" />
                    {cropRect && cropRect.w > 2 && cropRect.h > 2 && (
                        <div
                            className="absolute border-2 border-orange-400 bg-orange-400/10 pointer-events-none"
                            style={{
                                left: cropRect.x,
                                top: cropRect.y,
                                width: cropRect.w,
                                height: cropRect.h,
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Capture button */}
            <div className="mt-4 flex items-center gap-3">
                {cropRect && cropRect.w > 5 && cropRect.h > 5 && (
                    <span className="text-white/60 text-xs">
                        {Math.round(cropRect.w)} × {Math.round(cropRect.h)} px
                    </span>
                )}
                <button
                    onClick={handleCapture}
                    disabled={!cropRect || cropRect.w < 5 || cropRect.h < 5}
                    className="px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-lg"
                >
                    {language === "zh" ? "截取選取區域" : "Capture Selection"}
                </button>
            </div>
        </div>
    );
}

export default PdfCropModal;
